/**
 * Mirrors backend `INFERENCE_MEASUREMENT_ALLOWLIST` (measurement_allowlist.py).
 *
 * Resolution order (first non-empty wins):
 * 1. window.config.inferenceMeasurementAllowlist (runtime, e.g. public/config/local.js)
 * 2. window.RUNTIME_ENV.INFERENCE_MEASUREMENT_ALLOWLIST (container-injected)
 * 3. process.env.INFERENCE_MEASUREMENT_ALLOWLIST (webpack DefinePlugin at build time)
 */

declare global {
  interface Window {
    config?: { inferenceMeasurementAllowlist?: string };
    RUNTIME_ENV?: { INFERENCE_MEASUREMENT_ALLOWLIST?: string };
  }
}

const SHORT_TO_MEAS: Record<string, string> = {
  AC: 'MEAS__AC',
  ATD: 'MEAS__ATD',
  HC: 'MEAS__HC',
  BPD: 'MEAS__BPD',
  SDP: 'MEAS__SDP',
  FL: 'MEAS__FL',
  CRL: 'MEAS__CRL',
  CSP: 'MEAS__CSP_WIDTH',
  TCD: 'MEAS__TCD',
  CM: 'MEAS__CISTERNA_MAGNA',
  CISTERNA: 'MEAS__CISTERNA_MAGNA',
  NT: 'MEAS__NT',
  IOD: 'MEAS__IOD',
  OD: 'MEAS__ORBITAL_DIAMETER',
  ORBITAL: 'MEAS__ORBITAL_DIAMETER',
  CTR: 'MEAS__CTR',
  AORTA: 'MEAS__AORTA_DIAMETER',
  HL: 'MEAS__HL',
  TIBIA: 'MEAS__TIBIA_LENGTH',
  LV: 'MEAS__LV_DIAMETER',
  RV: 'MEAS__RV_DIAMETER',
  IVS: 'MEAS__IVS_THICKNESS',
  PA: 'MEAS__PA_DIAMETER',
  MV: 'MEAS__MITRAL_VALVE',
  TV: 'MEAS__TRICUSPID_VALVE',
  CL: 'MEAS__CERVICAL_LENGTH',
  CERVIX: 'MEAS__CERVICAL_LENGTH',
  PT: 'MEAS__PLACENTAL_THICKNESS',
  PLACENTA: 'MEAS__PLACENTAL_THICKNESS',
  PCD: 'MEAS__PCD',
  BLADDER: 'MEAS__BLADDER_DIAMETER',
  SBD: 'MEAS__STOMACH_BUBBLE_DIAMETER',
  STOMACH: 'MEAS__STOMACH_BUBBLE_DIAMETER',
  CP: 'MEAS__CHOROID_PLEXUS',
  CHOROID: 'MEAS__CHOROID_PLEXUS',
  LVW: 'MEAS__LATERAL_VENTRICLE_WIDTH',
  VENTRICLE: 'MEAS__LATERAL_VENTRICLE_WIDTH',
};

const LEGACY_MEAS = new Set<string>([
  'MEAS__AC',
  'MEAS__HC',
  'MEAS__SDP',
  'MEAS__FL',
]);

function parseToken(raw: string): string | null {
  const t = raw.trim();
  if (!t) {
    return null;
  }
  const upper = t.toUpperCase();
  if (upper.startsWith('MEAS__')) {
    return `MEAS__${t.slice(6).toUpperCase()}`;
  }
  return SHORT_TO_MEAS[upper.replace(/-/g, '_')] ?? null;
}

function getRawAllowlistString(): string {
  if (typeof window !== 'undefined') {
    const fromConfig = window.config?.inferenceMeasurementAllowlist;
    if (fromConfig != null && String(fromConfig).trim() !== '') {
      return String(fromConfig).trim();
    }
    const fromRuntime = window.RUNTIME_ENV?.INFERENCE_MEASUREMENT_ALLOWLIST;
    if (fromRuntime != null && String(fromRuntime).trim() !== '') {
      return String(fromRuntime).trim();
    }
  }
  if (typeof process !== 'undefined' && process.env?.INFERENCE_MEASUREMENT_ALLOWLIST) {
    return String(process.env.INFERENCE_MEASUREMENT_ALLOWLIST).trim();
  }
  return '';
}

let cachedRawInput: string | undefined;
let cachedParsed: ReadonlySet<string> | null | undefined;

function parseAllowlistRaw(raw: string): ReadonlySet<string> | null {
  if (!raw || ['*', 'all', 'full', 'ALL', 'FULL'].includes(raw)) {
    return null;
  }
  if (raw.toUpperCase() === 'LEGACY') {
    return LEGACY_MEAS;
  }
  const parts: string[] = [];
  for (const chunk of raw.replace(/;/g, ',').split(',')) {
    const c = chunk.trim();
    if (c) {
      parts.push(c);
    }
  }
  if (parts.length === 0) {
    return null;
  }
  const out = new Set<string>();
  for (const p of parts) {
    if (p.toUpperCase() === 'LEGACY') {
      LEGACY_MEAS.forEach((k) => out.add(k));
      continue;
    }
    const k = parseToken(p);
    if (k) {
      out.add(k);
    }
  }
  return out.size > 0 ? out : null;
}

/**
 * null → all MEAS__* keys allowed (same as unset / * / all / full on backend).
 */
export function getAllowedMeasurementKeys(): ReadonlySet<string> | null {
  const raw = getRawAllowlistString();
  if (cachedRawInput === raw && cachedParsed !== undefined) {
    return cachedParsed;
  }
  cachedRawInput = raw;
  cachedParsed = parseAllowlistRaw(raw);
  return cachedParsed;
}

export function measAllowed(
  allowed: ReadonlySet<string> | null,
  measKey: string
): boolean {
  return allowed === null || allowed.has(measKey);
}

/** Non-MEAS__ keys are always shown (structures / legacy labels). */
export function isMeasurementAllowed(measKey: string): boolean {
  if (!measKey.startsWith('MEAS__')) {
    return true;
  }
  return measAllowed(getAllowedMeasurementKeys(), measKey);
}
